/**
 * App Component
 *
 * Main application entry point and orchestrator.
 * Handles mode routing and global providers.
 */

import { useState, useEffect, useCallback, Suspense } from 'react';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { ImageGenerationPage } from './pages/ImageGenerationPage';
import { VideoPage } from './pages/VideoPage';
import { AudioPage } from './pages/AudioPage';
import { MiscellaneousPage } from './pages/MiscellaneousPage';
import { SettingsPage } from './pages/SettingsPage';
import { TokenPurchaseSuccess, TokenPurchaseCancelled } from './pages/TokensPage';
import { PricingPage } from './pages/PricingPage';
import { WhitepaperPage } from './pages/WhitepaperPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { AdminMetricsPage } from './pages/AdminMetricsPage';
import { ModelCapabilityPage } from './pages/ModelCapabilityPage';
import { VideoEditorPage } from './pages/VideoEditorPage';
import { DownloadPage } from './components/download';
import { BuyTokensModal } from './components/tokens';
import { JackpotModal } from './components/jackpot';
import { YouTubePage } from './pages/PublishPage';
import { OAuthCallbackPage } from './pages/OAuthCallbackPage';
import { AuthorizePage } from './pages/AuthorizePage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastContainer } from './components/Toast';
import { Sidebar, TopBar, Footer } from './components/layout';
import type { AppMode } from './components/layout';
import type { VideoConfig } from './types';
import { LoginModal, TermsOfServiceModal } from './components/auth';
import { FirstLaunchWizard } from './components/FirstLaunchWizard';
import { LinaAssistant } from './components/chat';
import { useBackendConnection } from './hooks/useBackendConnection';
import { useSetupWizard } from './hooks/useSetupWizard';
import { useToast } from './hooks/useToast';
import { useTranslation } from './hooks/useTranslation';
import { useAuth } from './hooks/useAuth';
import { useRequireAuth } from './hooks/useRequireAuth';
import { useElectronSettings } from './hooks/useElectronSettings';
import { audioNotification } from './utils/audioNotification';
import { isLocalMode, getAPIMode } from './utils/apiMode';
import { I18nProvider } from './i18n';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { TokenGateProvider } from './contexts/TokenGateContext';
import { TokenBalanceProvider } from './contexts/TokenBalanceContext';
import { GeoProvider } from './contexts/GeoContext';
import { GenerationProvider } from './contexts/GenerationContext';
import { STORAGE_KEYS } from './constants/storage';

const LandingPage = lazyWithRetry(() =>
  import('./pages/LandingPage').then((m) => ({ default: m.LandingPage }))
);

/** Modes that should show the footer */
const MODES_WITH_FOOTER: AppMode[] = [
  'video',
  'image',
  'audio',
  'youtube',
  'videoEditor',
  'misc',
  'download',
];

function AppContent() {
  const { t } = useTranslation();
  const { user, loading: authLoading, tosAccepted, tosLoading } = useAuth();
  const {
    isLoginModalOpen: isAuthRequiredModalOpen,
    loginMessage,
    closeLoginModal: closeAuthRequiredModal,
  } = useRequireAuth();

  const [mode, setMode] = useState<AppMode>('video');
  // Sub-tab to deep-link into for the next rendered section (set by navigate
  // events / billing links). Cleared on plain sidebar clicks.
  const [pendingSubTab, setPendingSubTab] = useState<string | undefined>(undefined);
  // Config pushed from the global Lina assistant. Held here (App is always
  // mounted) so it survives VideoPage mounting, then forwarded as a prop —
  // avoids a race where VideoPage's listener isn't attached yet.
  const [pendingVideoConfig, setPendingVideoConfig] = useState<Partial<VideoConfig> | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(audioNotification.isEnabled());
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [isBuyTokensModalOpen, setIsBuyTokensModalOpen] = useState(false);
  const [isJackpotModalOpen, setIsJackpotModalOpen] = useState(false);

  // URL-based routing for special pages
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Navigate to home (clear URL path)
  const navigateHome = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPath('/');
  }, []);

  const { isConnected: isBackendConnected } = useBackendConnection();
  const { toasts, showToast, removeToast } = useToast();

  // Plain navigation from the sidebar — clears any pending deep-link state.
  const handleSidebarNavigate = useCallback((nextMode: AppMode) => {
    setMode(nextMode);
    setPendingSubTab(undefined);
    setPendingVideoConfig(null);
  }, []);

  // Open buy tokens modal
  const openBuyTokensModal = useCallback(() => {
    setIsBuyTokensModalOpen(true);
  }, []);

  // Open jackpot modal
  const openJackpotModal = useCallback(() => {
    setIsJackpotModalOpen(true);
  }, []);

  // Electron "Preferences" menu opens the Settings page.
  useElectronSettings(
    useCallback(() => {
      setMode('settings');
      setPendingSubTab(undefined);
    }, [])
  );

  // Check if first launch (only for desktop/local mode, not serverless cloud)
  const { isFirstLaunch, isLoading: isWizardLoading } = useSetupWizard();

  // Show wizard on first launch in desktop mode only (not serverless/staging/production)
  useEffect(() => {
    const isDesktopMode = isLocalMode();

    console.log('[Wizard Debug]', {
      VITE_API_MODE: import.meta.env.VITE_API_MODE,
      VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
      apiMode: getAPIMode(),
      isDesktopMode,
      isWizardLoading,
      isFirstLaunch,
      localStorage: localStorage.getItem(STORAGE_KEYS.SETUP_WIZARD_COMPLETED),
    });

    if (isDesktopMode && !isWizardLoading && isFirstLaunch === true) {
      console.log('[Wizard Debug] Showing wizard!');
      setShowWizard(true);
    }
  }, [isFirstLaunch, isWizardLoading]);

  // Listen for navigate-to-mode events from child components.
  // Detail is either a bare mode string (legacy) or { mode, subTab }.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<AppMode | { mode: AppMode; subTab?: string }>).detail;
      if (!detail) return;
      if (typeof detail === 'string') {
        setMode(detail);
        setPendingSubTab(undefined);
      } else if (detail.mode) {
        setMode(detail.mode);
        setPendingSubTab(detail.subTab);
      }
    };
    window.addEventListener('navigate-to-mode', handler);
    return () => window.removeEventListener('navigate-to-mode', handler);
  }, []);

  // Token popover "billing history" link → Settings page, Billing sub-tab.
  useEffect(() => {
    const handler = () => {
      setMode('settings');
      setPendingSubTab('billing');
    };
    window.addEventListener('open-billing-history', handler);
    return () => window.removeEventListener('open-billing-history', handler);
  }, []);

  // Lina (global floating widget) parsed a video config → route to the Video
  // tab and forward the config as a prop (new object reference re-applies it).
  useEffect(() => {
    const handler = (e: Event) => {
      const config = (e as CustomEvent<Partial<VideoConfig>>).detail;
      if (!config) return;
      setMode('video');
      setPendingSubTab('full-video');
      setPendingVideoConfig({ ...config });
    };
    window.addEventListener('apply-video-config', handler);
    return () => window.removeEventListener('apply-video-config', handler);
  }, []);

  // Audio notification toggle
  const toggleAudio = () => {
    const newState = !audioEnabled;
    setAudioEnabled(newState);
    audioNotification.setEnabled(newState);
    showToast(
      'info',
      t('app.notifications.audioTitle'),
      newState ? t('app.notifications.audioEnabled') : t('app.notifications.audioDisabled'),
      2000
    );
  };

  // Listen for popstate to update currentPath (e.g., browser back/forward, landing footer links)
  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleCloseLoginModal = () => {
    setIsLoginModalOpen(false);
  };

  // Render the appropriate page based on mode, each wrapped in an ErrorBoundary
  const renderPage = () => {
    switch (mode) {
      case 'image':
        return (
          <ErrorBoundary section="image" key="image">
            <ImageGenerationPage />
          </ErrorBoundary>
        );
      case 'audio':
        return (
          <ErrorBoundary section="audio" key="audio">
            <AudioPage initialSubTab={pendingSubTab} />
          </ErrorBoundary>
        );
      case 'youtube':
        return (
          <ErrorBoundary section="youtube" key="youtube">
            <YouTubePage />
          </ErrorBoundary>
        );
      case 'videoEditor':
        return (
          <ErrorBoundary section="videoEditor" key="videoEditor">
            <VideoEditorPage />
          </ErrorBoundary>
        );
      case 'misc':
        return (
          <ErrorBoundary section="misc" key="misc">
            <MiscellaneousPage initialSubTab={pendingSubTab} />
          </ErrorBoundary>
        );
      case 'settings':
        return (
          <ErrorBoundary section="settings" key="settings">
            <SettingsPage initialSubTab={pendingSubTab} />
          </ErrorBoundary>
        );
      case 'download':
        return (
          <ErrorBoundary section="download" key="download">
            <DownloadPage />
          </ErrorBoundary>
        );
      case 'video':
      default:
        return (
          <ErrorBoundary section="video" key="video">
            <VideoPage
              isBackendConnected={isBackendConnected}
              initialSubTab={pendingSubTab}
              externalConfigUpdate={pendingVideoConfig}
            />
          </ErrorBoundary>
        );
    }
  };

  const showFooter = MODES_WITH_FOOTER.includes(mode);

  // Show first launch wizard
  if (showWizard) {
    return <FirstLaunchWizard onComplete={() => setShowWizard(false)} />;
  }

  // Handle special URL routes for token purchase results and pricing page
  console.log('[App] currentPath:', currentPath);
  if (currentPath === '/tokens/success') {
    console.log('[App] Rendering TokenPurchaseSuccess');
    return <TokenPurchaseSuccess onNavigateHome={navigateHome} />;
  }

  if (currentPath === '/tokens/cancelled') {
    const handleTryAgain = () => {
      navigateHome();
      // Small delay to allow navigation to complete before opening modal
      setTimeout(() => setIsBuyTokensModalOpen(true), 100);
    };
    return <TokenPurchaseCancelled onNavigateHome={navigateHome} onTryAgain={handleTryAgain} />;
  }

  // Pricing page route
  if (currentPath === '/pricing') {
    return (
      <PricingPage onNavigateHome={navigateHome} onLoginClick={() => setIsLoginModalOpen(true)} />
    );
  }

  // Whitepaper page route
  if (currentPath === '/whitepaper') {
    return <WhitepaperPage onNavigateHome={navigateHome} />;
  }

  // Privacy Policy page route (public — required by GDPR/LGPD/OAuth verification)
  if (currentPath === '/privacy') {
    return <PrivacyPolicyPage onNavigateHome={navigateHome} />;
  }

  // Terms of Service page route (public — re-readable copy of the acceptance modal)
  if (currentPath === '/terms') {
    return <TermsOfServicePage onNavigateHome={navigateHome} />;
  }

  // Admin metrics page route
  if (currentPath === '/admin/metrics') {
    return <AdminMetricsPage />;
  }

  // Admin model-capability registry page route
  if (currentPath === '/admin/model-capability') {
    return <ModelCapabilityPage />;
  }

  // Application-grant consent screen (integrationMode Stage 06).
  //
  // Placed here, ABOVE the authenticated app layout, on purpose: this page must
  // render on its own, with nothing around it. A consent screen wrapped in the
  // product's sidebar and top bar invites the user to read it as part of a
  // session they were already in, when the whole question it asks is whether to
  // let somebody ELSE act as them. It handles its own signed-out state.
  if (currentPath === '/authorize') {
    return (
      <>
        <AuthorizePage onSignInRequired={() => setIsLoginModalOpen(true)} />
        {/* The sign-in modal has to be mounted HERE too: this branch returns
            before the main layout that normally renders it, and a "Sign in"
            button that sets state nothing reads is a dead end on the one screen
            a user cannot skip. */}
        <LoginModal isOpen={isLoginModalOpen} onClose={handleCloseLoginModal} />
      </>
    );
  }

  // YouTube OAuth callback (web flow) — must run for both authed and pre-auth
  // users since Google may land them before Firebase rehydrates.
  if (currentPath === '/oauth/callback') {
    return <OAuthCallbackPage onNavigateHome={navigateHome} />;
  }

  // Landing page for unauthenticated users at root path
  if (currentPath === '/' && !user && !authLoading) {
    return (
      <Suspense
        fallback={
          <div className="min-h-screen bg-[#0a0015] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        }
      >
        <LandingPage
          onGetStarted={() => setIsLoginModalOpen(true)}
          onNavigateToPricing={() => {
            window.history.pushState({}, '', '/pricing');
            setCurrentPath('/pricing');
          }}
        />
        <LoginModal isOpen={isLoginModalOpen} onClose={handleCloseLoginModal} />
      </Suspense>
    );
  }

  return (
    <div className="app-layout">
      {/* Sidebar Navigation */}
      <Sidebar currentMode={mode} onModeChange={handleSidebarNavigate} />

      {/* Main Content Area */}
      <div className="app-content">
        {/* Top Bar */}
        <TopBar
          audioEnabled={audioEnabled}
          onToggleAudio={toggleAudio}
          onLoginClick={() => setIsLoginModalOpen(true)}
          onBuyTokensClick={openBuyTokensModal}
          onJackpotClick={openJackpotModal}
        />

        {/* Page Content */}
        {renderPage()}

        {/* Footer */}
        {showFooter && <Footer />}
      </div>

      {/* Lina AI assistant — global floating widget (persists across all tabs) */}
      <LinaAssistant />

      {/* Login Modal (manual sign in) */}
      <LoginModal isOpen={isLoginModalOpen} onClose={handleCloseLoginModal} />

      {/* Login Modal (auth required for actions) */}
      <LoginModal
        isOpen={isAuthRequiredModalOpen}
        onClose={closeAuthRequiredModal}
        message={loginMessage}
      />

      {/* Buy Tokens Modal */}
      <BuyTokensModal
        isOpen={isBuyTokensModalOpen}
        onClose={() => setIsBuyTokensModalOpen(false)}
      />

      {/* Jackpot Modal */}
      <JackpotModal
        isOpen={isJackpotModalOpen}
        onClose={() => setIsJackpotModalOpen(false)}
        onNavigateToPricing={() => {
          setIsJackpotModalOpen(false);
          window.history.pushState({}, '', '/pricing');
          setCurrentPath('/pricing');
        }}
      />

      {/* Terms of Service Modal (blocking - no dismiss) */}
      <TermsOfServiceModal isOpen={!!user && !authLoading && !tosLoading && !tosAccepted} />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary section="root">
      <ThemeProvider>
        <I18nProvider>
          <SettingsProvider>
            <AuthProvider>
              <TokenBalanceProvider>
                <GeoProvider>
                  <TokenGateProvider>
                    <GenerationProvider>
                      <AppContent />
                    </GenerationProvider>
                  </TokenGateProvider>
                </GeoProvider>
              </TokenBalanceProvider>
            </AuthProvider>
          </SettingsProvider>
        </I18nProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
